import ApiService from '@/services/ApiService.js'
import moment from 'moment'
import store from '@/store/store'
import swal from 'sweetalert'
import { podSerialNumberPrefix, filterFlag } from '@/const'
import { app } from '@/main.js'

export const namespaced = true
export const state = {
  patients: [],
  selectedPatient: '',
  selectedPatientGoal: {},
  selectedPatientData: {},
  selectedDate: moment(),
  selectedDateRange: [moment().add(-6, 'days'), moment()]
}

export const mutations = {
  SET_PATIENTS(state, value) {
    state.patients = value
  },
  SET_SELECTED_PATIENT(state, value) {
    state.selectedPatient = value
  },
  SET_SELECTED_PATIENT_GOAL(state, value) {
    state.selectedPatientGoal = value
  },
  SET_SELECTED_PATIENT_DATA(state, value) {
    state.selectedPatientData = value
  },
  SET_SELECTED_DATE(state, value) {
    state.selectedDate = value
  },
  SET_SELECTED_DATE_RANGE(state, value) {
    state.selectedDateRange = value
  }
}

export const getters = {
  patientsCount: state => {
    return state.patients.length
  }
}

export const actions = {
  createPatient({ commit }, patient) {
    return ApiService.postPatients(patient).then(() => {
      commit('ADD_PATIENT', patient)
    })
  },
  fetchPatients({ commit }) {
    return ApiService.getPatients(
      store.getters[`user/selecteOrganisationId`],
      filterFlag.all,
      []
    )
      .then(response => {
        let patients = response.data.data.map(item => {
          return {
            ...item,
            label: item.alias + ' (' + item.podMac.substr(-4) + ')'
          }
        })
        commit('SET_PATIENTS', patients)
        if (!this.state.selectedPatient && patients.length) {
          let patientsWithPod = patients.filter(item => item.podMac)
          if (patientsWithPod.length) {
            commit('SET_SELECTED_PATIENT', patientsWithPod[0])
          }
        }
      })
      .catch(() => {
        // console.log('IN Catch: ')
      })
  },
  savePatient(e, patient) {
    return new Promise((resolve, reject) => {
      ApiService.SavePatient(getPatientParams(patient))
        .then(response => {
          if (response.data.success) {
            swal({
              icon: 'success',
              title: app.$i18n.t('text.well_done'),
              text: patient.PatientId
                ? app.$i18n.t('text.you_have_updated_the_patient')
                : app.$i18n.t('text.you_have_created_the_patient'),
              buttons: {
                catch: {
                  text: app.$i18n.t('text.next_steps'),
                  value: 'catch'
                },
                defeat: {
                  text: app.$i18n.t('text.ok').toUpperCase(),
                  value: 'defeat'
                }
              }
            }).then(value => {
              if (value === 'catch') {
                let language = 'EN'
                switch (store.state.locale.currentLocale) {
                  case 'fr':
                    language = 'FR'
                    break
                  case 'it':
                    language = 'IT'
                    break
                  case 'de':
                    language = 'DE'
                    break
                  default:
                    language = 'EN'
                }
                window.open(
                  `https://www.smartzhealth.com/wp-content/uploads/2021/08/SAS_GEN1_0064_Smartz-Quick-Start-Guide_${language}.pdf`,
                  '_blank'
                )
              }
            })
          } else {
            let errorMsg = response.data.msg
            switch (response.data.errorcode) {
              case 318:
                errorMsg = app.$i18n.t('text.savePatientPodErrMsg')
                break
              case 321:
                errorMsg = app.$i18n.t('text.savePatientNodeErrMsg')
                break
              default:
                errorMsg = response.data.msg
            }
            swal({
              icon: 'error',
              title: app.$i18n.t('text.failed'),
              text: errorMsg
            })
          }
          resolve(response)
        })
        .catch(() => {
          swal({
            icon: 'error',
            timer: 2000,
            title: app.$i18n.t('text.failed')
          })
          reject(Error('Unknown Error'))
        })
    })
  },
  getSelectedPatientGoal({ commit }) {
    return new Promise((resolve, reject) => {
      if (!store.state.patient.selectedPatient) {
        commit('SET_SELECTED_PATIENT_GOAL', {})
        return
      }
      ApiService.GetPatientGoal(store.state.patient.selectedPatient.patientId)
        .then(response => {
          if (response.data.success) {
            commit('SET_SELECTED_PATIENT_GOAL', response.data.data.patientGoal)
          } else {
            swal({
              icon: 'error',
              timer: 2000,
              title: app.$i18n.t('text.failed'),
              text: response.data.msg
            })
          }
          resolve(response)
        })
        .catch(() => {
          swal({
            icon: 'error',
            timer: 2000,
            title: app.$i18n.t('text.failed'),
            text: app.$i18n.t('text.failed_to_get_goal_settings')
          })
          reject(Error('Unknown Error'))
        })
    })
  },
  getSelectedPatientHistoryData({ commit }) {
    return new Promise((resolve, reject) => {
      if (!store.state.patient.selectedPatient) {
        commit('SET_SELECTED_PATIENT_DATA', {})
        return
      }
      ApiService.GetPatientHistory(
        store.state.patient.selectedPatient.patientId
      )
        .then(response => {
          commit('SET_SELECTED_PATIENT_DATA', response.data)
          resolve(response)
        })
        .catch(() => {
          swal({
            icon: 'error',
            timer: 2000,
            title: app.$i18n.t('text.failed'),
            text: app.$i18n.t('text.failed_to_get_goal_settings')
          })
          reject(Error('Unknown Error'))
        })
    })
  }
}

function getPatientParams(params) {
  params.OrganisationId = store.getters[`user/selecteOrganisationId`]
  params.AllocatedPod = podSerialNumberPrefix + params.AllocatedPod
  params.PodAssignedDate = moment().utc()
  delete params.DayManufacturerId
  delete params.NightManufacturerId
  if (!params.PatientId) {
    delete params.PatientId
  }

  //if period selected is 24 hour, productid to be 0 ,starttime and endtime to be null
  if (
    params.DayStartTime &&
    params.DayEndTime &&
    params.DayStartTime === params.DayEndTime
  ) {
    params.NightStartTime = params.NightEndTime = null
    params.NightProductId = 0
  }
  if (
    params.NightStartTime &&
    params.NightEndTime &&
    params.NightStartTime === params.NightEndTime
  ) {
    params.DayStartTime = params.DayEndTime = null
    params.DayProductId = 0
  }
  return params
}
